import { AxiosRequestConfig, AxiosResponse, Method } from 'axios'
import { useQuery } from 'react-query'
import type { RetryValue } from 'react-query/types/core/retryer'

import { hermesService } from '@/api'

interface UseQueryHookParams<InitialDataType> {
    endpoint: string
    params?: AxiosRequestConfig['params']
    cacheKey?: string | any[]
    initialData?: InitialDataType
    enabled?: boolean
    method?: Method
    body?: any
    onSuccess?: (data: InitialDataType) => void
    onError?: (error: any) => void
    headers?: Record<string, unknown>
    retry?: RetryValue<string & Error>
    retryDelay?: (attemptIndex: number) => number
    refetchInterval?: number | false
    refetchOnWindowFocus?: boolean
    refetchIntervalInBackground?: boolean
    refetchOnReconnect?: boolean
    refetchOnMount?: boolean
    staleTime?: number
    timeout?: number
    initialFetchDisabled?: boolean
}

const useQueryHook = <DataType>({
    endpoint,
    params,
    cacheKey,
    initialData,
    headers,
    enabled = true,
    method = 'GET',
    body,
    onSuccess,
    onError,
    refetchInterval = false,
    refetchOnWindowFocus = false,
    refetchOnReconnect = false,
    refetchIntervalInBackground = false,
    refetchOnMount = false,
    // retries default to 3 in react-query
    retry = false,
    staleTime, // time after which data will be considered stale - defaults to 0
    timeout,
    // The default retryDelay is set to double (starting at 1000ms) with each attempt, but not exceed 30 seconds
    retryDelay = (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    initialFetchDisabled,
}: UseQueryHookParams<DataType>): {
    error?: string | null
    data?: DataType | undefined
    fetching: boolean
    loading: boolean
    refetch: () => void
} => {
    const { data, isLoading, isFetching, error, refetch } = useQuery<DataType, string & Error>(
        // The cache key should be an array. If it is not set, we generate it from the endpoint and params
        cacheKey || [endpoint, method, body, params, initialFetchDisabled],
        async (): Promise<DataType> => {
            let response: AxiosResponse
            if (method === 'GET') response = await hermesService.get(endpoint, { params, data: body, headers, timeout })
            else response = await hermesService.request({ method, url: endpoint, params, data: body, headers, timeout })
            return response?.data
        },
        {
            enabled,
            initialData,
            staleTime,
            refetchOnMount,
            refetchOnWindowFocus,
            refetchIntervalInBackground,
            refetchOnReconnect,
            retryDelay,
            retry,
            keepPreviousData: true,
            onSuccess,
            onError,
            refetchInterval,
        },
    )
    return {
        data,
        error: error?.message || error,
        loading: isLoading,
        fetching: isLoading || isFetching,
        refetch,
    }
}

export const serverSideQuery =
    <DataType, ParamType = AxiosRequestConfig['params']>(endpoint: string, headers?: { [key: string]: unknown }) =>
    async (params: ParamType, maxAgeMs?: number): Promise<DataType> => {
        try {
            const options = { params, headers, cache: maxAgeMs ? { maxAge: maxAgeMs } : undefined }
            const { data } = await hermesService.get(endpoint, options)
            return data
        } catch (e) {
            return Promise.reject(e)
        }
    }

export default useQueryHook
